<script setup lang="ts">
    import { inject, onMounted, reactive } from 'vue';
    import { refreshTranslations } from './BackOfficeRoot.access';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { FeatureService } from '@shared/directives/Feature.service';
    import SidebarMenu, { SidebarMenuGroup } from '@shared/components/SidebarMenu.vue';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const featureAccess: FeatureService = injectorAccess.injector.get(FeatureService);

    const state = reactive<{
        refreshingTranslations: boolean;
    }>({
        refreshingTranslations: false,
    });

    onMounted(async () => {
        if (!featureAccess.hasFeature('BackOffice')) {
            window.location.href = '/';
        }
    });

    async function refreshTranslationsClicked(): Promise<void> {
        state.refreshingTranslations = true;

        await refreshTranslations().then(() => {
            location.reload();
        });
    }

    const menuGroups: SidebarMenuGroup[] = [
        {
            heading: 'Start',
            items: [
                { routeNames: ['BackOfficeDashboard'], label: 'Dashboard' },
                { routeNames: ['SubscriptionStats'], label: 'Subscription Stats' },
                { routeNames: ['UnsubscribeReasonStats'], label: 'Unsubscribe Stats' },
            ],
        },
        {
            heading: 'Accounts',
            items: [
                { routeNames: ['AccountList', 'AccountEdit'], label: 'Accounts' },
                { routeNames: ['SubscriptionList', 'SubscriptionEdit'], label: 'Subscriptions' },
                { routeNames: ['UserList', 'UserEdit'], label: 'Users' },
                { routeNames: ['SubscriptionsDashboard'], label: 'Subscriptions dashboard' },
                {
                    routeNames: [
                        'BillingRenewals',
                        'BillingOrders',
                        'BillingInvoices',
                        'BillingRegisterPayment',
                        'BillingUnresolved',
                        'BillingAccountsReceivable',
                        'BillingMonthlyReport',
                        'BillingNeedToPay',
                        'BillingTransactions',
                    ],
                    label: 'Billing',
                },
            ],
        },
        {
            heading: 'System',
            items: [
                { routeNames: ['Features'], label: 'Features' },
                { routeNames: ['FeatureSets'], label: 'Feature sets' },
                { routeNames: ['FeatureFlags'], label: 'Feature flags' },
                { routeNames: ['HealthManager'], label: 'Health monitor' },
                { routeNames: ['Miscellaneous'], label: 'Miscellaneous' },
            ],
        },
        {
            heading: 'Managing',
            items: [
                { routeNames: ['ManageUsers'], label: 'Manage users' },
                { routeNames: ['TaskManager'], label: 'Manage tasks' },
                { routeNames: ['MessageManager'], label: 'Manage messages' },
                { routeNames: ['ManageWorkers'], label: 'Manage workers' },
                { routeNames: ['OAuthProviders'], label: 'Manage OAuthProviders' },
                { routeNames: ['LatestSms'], label: 'Latest sent sms' },
                { routeNames: ['LatestIssues'], label: 'Latest sent mailings' },
            ],
        },
        {
            heading: 'Configuration',
            items: [
                { routeNames: ['Pricing'], label: 'Pricing' },
                { routeNames: ['GlobalTemplates'], label: 'Templates' },
                { routeNames: ['SmtpServers'], label: 'Smtp servers' },
                { routeNames: ['Deliverability'], label: 'Domain Validation' },
                { routeNames: ['Administrators'], label: 'Back Office Administrators' },
            ],
        },
    ];
</script>

<template>
    <div class="has-sidebar">
        <nav role="navigation" class="sidebar set-height custom-scrollbar navbar-sticky">
            <p class="strong">Back Office</p>

            <p>
                <a href="/OldBackOffice">Legacy Back Office</a>
            </p>

            <div class="form-group m-md-b">
                <button
                    class="btn btn-default btn-xs btn-block"
                    type="button"
                    :disabled="state.refreshingTranslations"
                    :class="{ 'is-busy': state.refreshingTranslations }"
                    @click="refreshTranslationsClicked">
                    {{ $Resources.RefreshTranslations }}
                </button>
            </div>

            <SidebarMenu :groups="menuGroups" />
        </nav>

        <section>
            <router-view></router-view>
        </section>
    </div>
</template>
